import { keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { type FiltersTrackingLocation, useTrackFiltersApplied, useTrackFiltersRemoved } from '@air/analytics';

import { type CustomFieldFilterType, type FilterType } from '~/components/FiltersMenu/types';
import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { removeFiltersAction } from '~/store/filters/actions';
import { filtersValuesSelector, selectedFiltersSelector } from '~/store/filters/selectors';
import { filterParamsToTrackingType, getUrlParamsFromFilters } from '~/utils/filters/filtersToUrlParamsUtils';
import { unresolvedPlainTextCustomFieldsToClear } from '~/utils/filters/urlParamsToFiltersUtils';
import { useAirDeepEqualSelector, useAirStore } from '~/utils/ReduxUtils';

export const useFiltersDropdownController = ({
  availableFilters,
  doAfterApply,
  customFields = [],
  trackLocation,
}: {
  availableFilters: FilterType[];
  customFields?: CustomFieldFilterType[];
  doAfterApply?: () => void;
  trackLocation: FiltersTrackingLocation;
}) => {
  const dispatch = useDispatch();
  const { applyFilters } = useApplyFilters();
  const store = useAirStore();
  const allSelectedFilters = useAirDeepEqualSelector(selectedFiltersSelector);
  const { trackFiltersRemoved } = useTrackFiltersRemoved();
  const { trackFiltersApplied } = useTrackFiltersApplied();

  const selectedFilters = useMemo(() => {
    const customFieldsById = keyBy(customFields, 'id');

    return allSelectedFilters.filter((f) => {
      if (f.type === 'customField' && f.customField) {
        return !!customFieldsById[f.customField.id] || !!f.unresolvedPlainText;
      } else {
        return availableFilters.includes(f.type);
      }
    });
  }, [availableFilters, customFields, allSelectedFilters]);

  const applySelectedFilters = useCallback(async () => {
    const filters = filtersValuesSelector(store.getState());
    await applyFilters({ filters });
    const appliedFilters = getUrlParamsFromFilters(filters);
    const appliedTypes = filterParamsToTrackingType(appliedFilters);

    if (appliedTypes.length) {
      trackFiltersApplied({
        types: filterParamsToTrackingType(appliedFilters),
        location: trackLocation,
        builderType: 'filter-builder',
      });
    } else {
      trackFiltersRemoved({
        builderType: 'filter-builder',
        location: trackLocation,
        types: [],
      });
    }
    doAfterApply?.();
  }, [applyFilters, doAfterApply, store, trackLocation, trackFiltersRemoved, trackFiltersApplied]);

  const clearSelectedFilters = useCallback(async () => {
    const unresolvedSelected = unresolvedPlainTextCustomFieldsToClear({
      catalogFieldIds: new Set(customFields.map((field) => field.id)),
      selectedFilters: allSelectedFilters,
    });
    const customFieldFilter: FilterType = 'customField';
    const filtersToClear =
      unresolvedSelected.length > 0 && !availableFilters.includes(customFieldFilter)
        ? [...availableFilters, customFieldFilter]
        : availableFilters;

    dispatch(
      removeFiltersAction({
        filters: filtersToClear,
        customFields: [...customFields, ...unresolvedSelected],
      }),
    );
    await applySelectedFilters();
  }, [allSelectedFilters, applySelectedFilters, availableFilters, customFields, dispatch]);

  return {
    selectedFilters,
    applySelectedFilters,
    clearSelectedFilters,
  };
};
